// Shared OASIS schema used by both the Coder (FullOasis form) and QA (AuditForm) pages.

export const ASSESSMENT_TYPES = [
  "Start of Care", "Resumption of Care", "Recertification", "Follow-up", "Discharge", "Transfer",
];

// Standardized GG performance code option set (used by every GG0130 / GG0170 sub-item)
export const GG_PERFORMANCE_CODES = [
  "06 - Independent",
  "05 - Setup or clean-up assistance",
  "04 - Supervision or touching assistance",
  "03 - Partial / moderate assistance",
  "02 - Substantial / maximal assistance",
  "01 - Dependent",
  "07 - Patient refused",
  "09 - Not applicable",
  "10 - Not attempted due to environmental limitations",
  "88 - Not attempted due to medical condition / safety concerns",
];

const ggItem = (id, label) => ({ id, label, type: "radio", options: GG_PERFORMANCE_CODES });

// Standardized J0510/J0520/J0530 pain-frequency response set
export const PAIN_FREQUENCY_OPTIONS = [
  "0 - Does not apply – no pain or hurting in the past 5 days",
  "1 - Rarely or not at all",
  "2 - Occasionally",
  "3 - Frequently",
  "4 - Almost constantly",
  "8 - Unable to answer",
];

const painItem = (id, label) => ({ id, label, type: "radio", options: PAIN_FREQUENCY_OPTIONS });

// Each section: array of { id, label, type, options? }
// type: text | textarea | number | date | select | radio | checklist | diagnosis_row
export const SECTIONS = [
  {
    key: "diagnoses",
    title: "Diagnoses (M1021 / M1023)",
    items: [
      { id: "M1021", label: "M1021 — Primary Diagnosis", type: "diagnosis_row" },
      ...Array.from({ length: 25 }, (_, i) => ({
        id: `M1023_${String.fromCharCode(65 + i)}`,
        label: `M1023 ${String.fromCharCode(65 + i)} — Other Diagnosis`,
        type: "diagnosis_row",
      })),
    ],
  },
  {
    key: "comorbidities",
    title: "Comorbidities & Therapies",
    items: [
      { id: "M1028", label: "M1028 — Active Diagnoses", type: "checklist", options: ["Peripheral Vascular Disease", "Diabetes Mellitus", "None of the above"] },
      { id: "M1033", label: "M1033 — Risk of Hospitalization (Select all that apply)", type: "checklist", options: [
        "1 - History of falls (2 or more falls — or any fall with injury — in the past 12 months)",
        "2 - Unintentional weight loss of 10 pounds or more in the past 12 months",
        "3 - Multiple hospitalizations (2 or more) in the past 6 months",
        "4 - Multiple emergency department visits (2 or more) in the past 6 months",
        "5 - Decline in mental, emotional, or behavioral status in the past 3 months",
        "6 - Reported or observed difficulty complying with medical instructions in the past 3 months",
        "7 - Currently taking 5 or more medications",
        "8 - Currently reports exhaustion",
        "9 - Other risk(s) not listed above",
        "10 - None of the below apply",
      ] },
    ],
  },
  {
    key: "integumentary",
    title: "Integumentary Status",
    items: [
      { id: "M1306", label: "M1306 — Unhealed Pressure Ulcer Stage 2+", type: "radio", options: ["0 - No", "1 - Yes"] },
      { id: "M1311", label: "M1311 — Count of Unhealed Pressure Ulcers", type: "number" },
      { id: "M1322", label: "M1322 — Stage 1 Pressure Injuries", type: "number" },
      { id: "M1324", label: "M1324 — Stage of Most Problematic Unhealed Pressure Ulcer/Injury that is Stageable", type: "radio", options: [
        "1 - Stage 1", "2 - Stage 2", "3 - Stage 3", "4 - Stage 4",
        "NA - No stageable pressure ulcer/injury",
      ] },
      { id: "M1330", label: "M1330 — Does this patient have a Stasis Ulcer?", type: "radio", options: [
        "0 - No",
        "1 - Yes, has both observable and unobservable",
        "2 - Yes, has observable only",
        "3 - Yes, has unobservable only (e.g., covered with non-removable dressing)",
      ] },
      { id: "M1332", label: "M1332 — Current Number of Stasis Ulcer(s) that are Observable", type: "radio", options: ["1 - One", "2 - Two", "3 - Three", "4 - Four or more"] },
      { id: "M1334", label: "M1334 — Status of Most Problematic Stasis Ulcer that is Observable", type: "radio", options: [
        "0 - Newly epithelialized", "1 - Fully granulating", "2 - Early / partial granulation", "3 - Not healing",
      ] },
      { id: "M1340", label: "M1340 — Does this patient have a Surgical Wound?", type: "radio", options: [
        "0 - No",
        "1 - Yes, patient has at least one observable surgical wound",
        "2 - Surgical wound known but not observable (non-removable dressing/device)",
      ] },
      { id: "M1342", label: "M1342 — Status of Most Problematic Surgical Wound that is Observable", type: "radio", options: [
        "0 - Newly epithelialized", "1 - Fully granulating", "2 - Early / partial granulation", "3 - Not healing",
      ] },
    ],
  },
  {
    key: "respiratory",
    title: "Respiratory Status",
    items: [
      { id: "M1400", label: "M1400 — Short of Breath", type: "radio", options: ["Never", "When walking >20 ft / climbing stairs", "With moderate exertion", "With minimal exertion", "At rest"] },
      { id: "M1410", label: "M1410 — Respiratory Treatments", type: "checklist", options: ["Oxygen", "Ventilator", "CPAP/BiPAP", "None of the above"] },
    ],
  },
  {
    key: "elimination",
    title: "Elimination Status",
    items: [
      { id: "M1600", label: "M1600 — Has this patient been treated for a Urinary Tract Infection in the past 14 days?", type: "radio", options: [
        "0 - No", "1 - Yes", "NA - Patient on prophylactic treatment", "UK - Unknown",
      ] },
      { id: "M1610", label: "M1610 — Urinary Incontinence / Catheter", type: "radio", options: ["No incontinence", "Incontinent", "Catheter"] },
      { id: "M1620", label: "M1620 — Bowel Incontinence Frequency", type: "radio", options: ["Never", "Less than once weekly", "1-3 times weekly", "4-6 times weekly", "Daily"] },
    ],
  },
  {
    key: "adl",
    title: "ADL / IADL",
    items: [
      { id: "M1800", label: "M1800 — Grooming", type: "radio", options: [
        "0 - Able to groom self unaided, with or without assistive devices or adapted methods",
        "1 - Grooming utensils must be placed within reach before able to complete grooming activities",
        "2 - Someone must assist the patient to groom self",
        "3 - Patient depends entirely upon someone else for grooming needs",
      ] },
      { id: "M1810", label: "M1810 — Current Ability to Dress Upper Body", type: "radio", options: [
        "0 - Able to dress upper body without assistance",
        "1 - Able to dress upper body without assistance if clothing is laid out or handed to the patient",
        "2 - Someone must help the patient put on upper body clothing",
        "3 - Patient depends entirely upon another person to dress the upper body",
      ] },
      { id: "M1820", label: "M1820 — Current Ability to Dress Lower Body", type: "radio", options: [
        "0 - Able to dress lower body without assistance",
        "1 - Able to dress lower body without assistance if clothing is laid out or handed to the patient",
        "2 - Someone must help the patient put on lower body clothing",
        "3 - Patient depends entirely upon another person to dress the lower body",
      ] },
      { id: "M1830", label: "M1830 — Bathing", type: "radio", options: [
        "0 - Able to bathe self independently in shower or tub, including getting in and out safely",
        "1 - With the use of devices, able to bathe self in shower or tub independently, including getting in and out safely",
        "2 - Able to bathe in shower or tub with intermittent assistance of another person for getting in/out or for washing difficult-to-reach areas",
        "3 - Able to participate in bathing in shower or tub, but requires presence of another person throughout the bath for assistance or supervision",
        "4 - Unable to use shower or tub, but able to participate in bathing self at sink/chair/bed with assistance or supervision",
        "5 - Unable to participate effectively in bathing and is bathed totally by another person",
      ] },
      { id: "M1840", label: "M1840 — Toilet Transferring", type: "radio", options: [
        "0 - Able to get to and from the toilet and transfer independently with or without an assistive device",
        "1 - When reminded, assisted, or supervised, able to get to and from the toilet and transfer",
        "2 - Unable to get to and from the toilet but is able to use a bedside commode (with or without assistance)",
        "3 - Unable to get to and from the toilet or bedside commode but is able to use a bedpan/urinal independently",
        "4 - Is totally dependent in toileting",
      ] },
      { id: "M1850", label: "M1850 — Transferring", type: "radio", options: [
        "0 - Able to independently transfer",
        "1 - Able to transfer with minimal human assistance or with the use of an assistive device",
        "2 - Able to bear weight and pivot during the transfer process but unable to transfer self",
        "3 - Unable to transfer self and is unable to bear weight or pivot when transferred by another person",
        "4 - Bedfast, unable to transfer but is able to turn and position self in bed",
        "5 - Bedfast, unable to transfer and is unable to turn and position self",
      ] },
      { id: "M1860", label: "M1860 — Ambulation / Locomotion", type: "radio", options: [
        "0 - Able to independently walk on even and uneven surfaces and negotiate stairs with or without assistive devices",
        "1 - Requires only minimal human assistance or supervision to walk safely",
        "2 - Requires the use of a one-handed device (e.g., cane, crutch) to walk independently or requires supervision",
        "3 - Requires the use of a two-handed device (e.g., walker) to walk independently or requires supervision",
        "4 - Chairfast, unable to ambulate, but able to wheel self independently",
        "5 - Chairfast, unable to ambulate, and unable to wheel self independently",
        "6 - Bedfast, unable to ambulate or be up in a chair",
      ] },
    ],
  },
  {
    key: "functional_gg",
    title: "Functional Abilities & Goals (GG0130 / GG0170)",
    items: [
      ggItem("GG0130A", "GG0130A — Self-Care: Eating"),
      ggItem("GG0130B", "GG0130B — Self-Care: Oral hygiene"),
      ggItem("GG0130C", "GG0130C — Self-Care: Toileting hygiene"),
      ggItem("GG0130E", "GG0130E — Self-Care: Shower / bathe self"),
      ggItem("GG0130F", "GG0130F — Self-Care: Upper body dressing"),
      ggItem("GG0130G", "GG0130G — Self-Care: Lower body dressing"),
      ggItem("GG0130H", "GG0130H — Self-Care: Putting on / taking off footwear"),
      ggItem("GG0170A", "GG0170A — Mobility: Roll left and right"),
      ggItem("GG0170B", "GG0170B — Mobility: Sit to lying"),
      ggItem("GG0170C", "GG0170C — Mobility: Lying to sitting on side of bed"),
      ggItem("GG0170D", "GG0170D — Mobility: Sit to stand"),
      ggItem("GG0170E", "GG0170E — Mobility: Chair / bed-to-chair transfer"),
      ggItem("GG0170F", "GG0170F — Mobility: Toilet transfer"),
      ggItem("GG0170G", "GG0170G — Mobility: Car transfer"),
      ggItem("GG0170I", "GG0170I — Mobility: Walk 10 feet"),
      ggItem("GG0170J", "GG0170J — Mobility: Walk 50 feet with two turns"),
      ggItem("GG0170K", "GG0170K — Mobility: Walk 150 feet"),
      ggItem("GG0170L", "GG0170L — Mobility: Walking 10 feet on uneven surfaces"),
      ggItem("GG0170M", "GG0170M — Mobility: 1 step (curb)"),
      ggItem("GG0170N", "GG0170N — Mobility: 4 steps"),
      ggItem("GG0170O", "GG0170O — Mobility: 12 steps"),
      ggItem("GG0170P", "GG0170P — Mobility: Picking up object"),
    ],
  },
  {
    key: "pain",
    title: "Pain Assessment (J0510 / J0520 / J0530)",
    items: [
      painItem("J0510", "J0510 — Pain Effect on Sleep"),
      painItem("J0520", "J0520 — Pain Interference with Therapy Activities"),
      painItem("J0530", "J0530 — Pain Interference with Day-to-Day Activities"),
    ],
  },
  {
    key: "special_treatments",
    title: "Special Treatments & Nutritional Approaches",
    items: [
      { id: "K0520", label: "K0520 — Nutritional Approaches", type: "checklist", options: [
        "A - Parenteral / IV feeding", "B - Feeding tube", "C - Mechanically altered diet",
        "D - Therapeutic diet", "Z - None of the above",
      ] },
      { id: "O0110", label: "O0110 — Special Treatments, Procedures, and Programs", type: "checklist", options: [
        "A1 - Chemotherapy", "A2 - Chemotherapy: IV", "A3 - Chemotherapy: Oral", "A10 - Chemotherapy: Other",
        "B1 - Radiation",
        "C1 - Oxygen Therapy", "C2 - Oxygen: Continuous", "C3 - Oxygen: Intermittent", "C4 - Oxygen: High-concentration",
        "D1 - Suctioning", "D2 - Suctioning: Scheduled", "D3 - Suctioning: As Needed",
        "E1 - Tracheostomy care",
        "F1 - Invasive Mechanical Ventilator (Ventilator / Respirator)",
        "G1 - Non-invasive Mechanical Ventilator", "G2 - BiPAP", "G3 - CPAP",
        "H1 - IV Medications", "H2 - IV: Vasoactive Medications", "H3 - IV: Antibiotics", "H4 - IV: Anticoagulation", "H10 - IV: Other",
        "I1 - Transfusions",
        "J1 - Dialysis", "J2 - Dialysis: Hemodialysis", "J3 - Dialysis: Peritoneal Dialysis",
        "O1 - IV Access", "O2 - IV Access: Peripheral", "O3 - IV Access: Mid-line", "O4 - IV Access: Central (PICC, Tunneled, Port)",
        "Z1 - None of the Above",
      ] },
    ],
  },
  {
    key: "medications",
    title: "Medications",
    items: [
      { id: "M2010", label: "M2010 — Patient/Caregiver High-Risk Drug Education", type: "radio", options: ["Yes", "No", "N/A"] },
      { id: "M2020", label: "M2020 — Management of Oral Medications", type: "radio", options: ["Independent", "With reminders", "Unable"] },
      { id: "M2030", label: "M2030 — Management of Injectable Medications", type: "radio", options: ["Independent", "With set-up help", "Unable", "No injectable meds"] },
      { id: "N0415", label: "N0415 — High-Risk Drug Classes: Use and Indication", type: "checklist", options: [
        "A - Antipsychotic (use + indication on record)",
        "B - Anticoagulant (use + indication on record)",
        "C - Antibiotic (use + indication on record)",
        "D - Opioid (use + indication on record)",
        "E - Antiplatelet (use + indication on record)",
        "F - Hypoglycemic / Insulin (use + indication on record)",
        "G - Diuretic (use + indication on record)",
        "Z - None of the above medication classes",
      ] },
    ],
  },
  {
    key: "care_mgmt",
    title: "Care Management",
    items: [
      { id: "M2102_a", label: "M2102 — Caregiver assistance: ADL", type: "radio", options: ["No assistance", "Daily assistance", "Less than daily"] },
      { id: "M2200", label: "M2200 — Therapy Need (combined therapy visits)", type: "number" },
      { id: "notes", label: "Additional notes", type: "textarea" },
    ],
  },
];

// Coding-specific error categories (used in the Coding section of the audit form)
export const CODING_ERROR_CATEGORIES = [
  "Primary Diagnosis Errors",
  "Secondary Diagnosis Errors",
  "Sequencing Errors",
  "ICD-10-CM Guideline Errors",
  "Unsupported Diagnosis",
  "Missing Diagnosis Codes",
  "Documentation Deficiency",
  "Other Coding Errors",
];

// OASIS-specific error categories (used in the OASIS section of the audit form)
export const OASIS_ERROR_CATEGORIES = [
  "M-Item Errors",
  "Functional Assessment Errors",
  "Clinical Assessment Errors",
  "Wound Assessment Errors",
  "Medication Assessment Errors",
  "OASIS Guideline Errors",
  "Documentation Deficiency",
  "Other OASIS Errors",
];

// Comprehensive QA error categories — kept for backward compatibility / legacy single-list flows.
export const AUDIT_ERROR_CATEGORIES = [
  // Coding / Diagnosis
  "Coding — M1021 Primary Diagnosis (wrong selection)",
  "Coding — M1023 Other Diagnoses (wrong selection)",
  "Coding — Sequencing",
  "Coding — Specificity (4th/5th/6th digit)",
  "Coding — Laterality",
  "Coding — Etiology / Manifestation",
  "Coding — Excludes1 / Excludes2 violation",
  "Coding — Z-code used as Primary (not allowed)",
  "Coding — Diagnosis Date error",
  "Coding — O/E (Onset/Exacerbation) incorrect",
  "Coding — Severity rating incorrect",
  "Coding Guidelines",
  // OASIS — M items
  "OASIS — M1028 / M1033 Comorbidities",
  "OASIS — M1306 / M1311 / M1322 Pressure Ulcers",
  "OASIS — M1324 Pressure Ulcer Stage",
  "OASIS — M1330 / M1332 / M1334 Stasis Ulcer",
  "OASIS — M1340 / M1342 Surgical Wound",
  "OASIS — M1400 / M1410 Respiratory",
  "OASIS — M1600 UTI",
  "OASIS — M1610 / M1620 Elimination",
  "OASIS — M1800–M1860 ADL / IADL",
  "OASIS — M2010 / M2020 / M2030 Medications",
  "OASIS — M2102 / M2200 Care Management",
  // OASIS — GG / J / K / N / O items
  "OASIS — GG0130 Self-Care Scoring",
  "OASIS — GG0170 Mobility Scoring",
  "OASIS — GG vs M-item Discrepancy",
  "OASIS — J0510 / J0520 / J0530 Pain Assessment",
  "OASIS — K0520 Nutritional Approach",
  "OASIS — N0415 High-Risk Drug Class",
  "OASIS — O0110 Special Treatments",
  // Cross-cutting OASIS
  "OASIS — Skip Pattern / Required Item Missing",
  "OASIS — Response Inconsistent with Diagnosis",
  "OASIS — Reason for Assessment Mismatch",
  // Documentation
  "Documentation — Insufficient",
  "Documentation — Contradictory",
  "Documentation — Missing",
  "Other",
];
